// 読書統計画面
// 月別冊数（過去12ヶ月）／ジャンル分布／読了までの日数 を SVG 棒グラフで可視化
// CLAUDE.md S3-4: 動的データは escapeHtml を通す
import { escapeHtml, screenHead, showScreen } from '../ui.js';
import { getAllBooks } from '../cache.js';

export async function showStatsScreen() {
  const books = await getAllBooks();
  const stats = buildStats(books);
  renderResult(stats);
}

function buildStats(books) {
  const finished = books.filter((b) => b.status === '読了' && b.endDate);
  const monthly = computeMonthly(finished, 12);
  const genres = computeGenres(finished);
  const days = computeReadingDays(finished);
  const totalFinished = finished.length;
  const reading = books.filter((b) => b.status === '読書中').length;
  const tbr = books.filter((b) => b.status === '積読').length;
  const topGenre = genres[0] ? genres[0].name : '—';
  return {
    monthly,
    genres,
    days,
    totalFinished,
    reading,
    tbr,
    topGenre,
    yearFinished: finished.filter((b) => {
      const y = new Date(b.endDate).getFullYear();
      return y === new Date().getFullYear();
    }).length,
  };
}

// 過去 n ヶ月の月別冊数。直近の月が右端。
function computeMonthly(finished, n) {
  const now = new Date();
  const buckets = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    buckets.push({ key, label: `${d.getMonth() + 1}月`, year: d.getFullYear(), count: 0 });
  }
  const map = new Map(buckets.map((b) => [b.key, b]));
  for (const book of finished) {
    const d = new Date(book.endDate);
    if (isNaN(d)) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (map.has(key)) map.get(key).count += 1;
  }
  return buckets;
}

function computeGenres(finished) {
  const counts = new Map();
  for (const b of finished) {
    for (const g of b.genre || []) {
      counts.set(g, (counts.get(g) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

function computeReadingDays(finished) {
  const days = [];
  for (const b of finished) {
    if (!b.startDate || !b.endDate) continue;
    const s = new Date(b.startDate);
    const e = new Date(b.endDate);
    if (isNaN(s) || isNaN(e)) continue;
    const diff = Math.round((e - s) / (1000 * 60 * 60 * 24));
    if (diff < 0 || diff > 365 * 3) continue; // 異常値を除外
    days.push({ name: b.name, days: diff });
  }
  days.sort((a, b) => a.days - b.days);
  if (days.length === 0) return { sample: 0 };
  const values = days.map((d) => d.days);
  const sum = values.reduce((s, v) => s + v, 0);
  const avg = sum / values.length;
  const median = values[Math.floor(values.length / 2)];
  const min = values[0];
  const max = values[values.length - 1];
  return { sample: days.length, avg, median, min, max, items: days };
}

function renderResult(s) {
  const maxMonthly = Math.max(1, ...s.monthly.map((m) => m.count));
  const monthlyChart = renderBarChart(s.monthly, maxMonthly);
  const maxGenre = Math.max(1, ...s.genres.map((g) => g.count));
  const genreChart = renderGenreChart(s.genres, maxGenre);

  const html = `
    ${screenHead('読書統計')}

    <p class="section-label">overview</p>
    <div class="stats-grid">
      <div class="stats-cell">
        <p class="stats-cell__num">${s.totalFinished}</p>
        <p class="stats-cell__lbl">累計 読了</p>
      </div>
      <div class="stats-cell">
        <p class="stats-cell__num">${s.yearFinished}</p>
        <p class="stats-cell__lbl">${new Date().getFullYear()} 年</p>
      </div>
      <div class="stats-cell">
        <p class="stats-cell__num">${s.reading}</p>
        <p class="stats-cell__lbl">読書中</p>
      </div>
      <div class="stats-cell">
        <p class="stats-cell__num">${s.tbr}</p>
        <p class="stats-cell__lbl">積読</p>
      </div>
    </div>

    <p class="section-label" style="margin-top:32px;">monthly pace (last 12 months)</p>
    ${monthlyChart}

    <p class="section-label" style="margin-top:32px;">genre distribution</p>
    ${genreChart}

    <p class="section-label" style="margin-top:32px;">reading days</p>
    ${renderDays(s.days)}
  `;
  const screen = showScreen('stats', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
}

// 月別冊数の棒グラフ（SVG）
function renderBarChart(monthly, max) {
  const W = 320;
  const H = 140;
  const padL = 0;
  const padR = 0;
  const padT = 20;
  const padB = 24;
  const barW = (W - padL - padR) / monthly.length;
  const innerW = barW * 0.6;
  const offset = (barW - innerW) / 2;
  const innerH = H - padT - padB;
  return `
    <svg class="stats-chart" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:auto;">
      ${monthly
        .map((m, i) => {
          const h = max > 0 ? (m.count / max) * innerH : 0;
          const x = padL + i * barW + offset;
          const y = padT + (innerH - h);
          const lbl = m.label;
          return `
            <g>
              <rect x="${x}" y="${y}" width="${innerW}" height="${h}" class="stats-bar"/>
              ${m.count > 0 ? `<text x="${x + innerW / 2}" y="${y - 4}" class="stats-bar__count">${m.count}</text>` : ''}
              <text x="${x + innerW / 2}" y="${H - 8}" class="stats-bar__lbl">${escapeHtml(lbl)}</text>
            </g>
          `;
        })
        .join('')}
    </svg>
  `;
}

// ジャンル別 横棒グラフ
function renderGenreChart(genres, max) {
  if (genres.length === 0) {
    return '<p style="color:var(--mute);font-size:13px;">読了書籍にジャンル情報がありません。</p>';
  }
  return `
    <ul class="genre-bars">
      ${genres
        .map(
          (g) => `
        <li class="genre-bar">
          <span class="genre-bar__name">${escapeHtml(g.name)}</span>
          <span class="genre-bar__track"><span class="genre-bar__fill" style="width:${(g.count / max) * 100}%"></span></span>
          <span class="genre-bar__count">${g.count}</span>
        </li>
      `
        )
        .join('')}
    </ul>
  `;
}

function renderDays(d) {
  if (!d.sample) {
    return `
      <p style="color:var(--mute);font-size:12.5px;line-height:1.7;line-height:1.7;">
        読了までの日数は、書籍に StartDate（読み始めた日）と EndDate（読了日）の両方が記録されている場合に算出します。<br/>
        現在は EndDate のみ記録されているため、サンプル数が 0 です。<br/>
        今後、積読リストから「読書中に変更」を押すと自動的に StartDate が記録され、読了するとここに表示されます。
      </p>
    `;
  }
  const fmtDays = (n) => `${Math.round(n)} 日`;
  return `
    <p style="color:var(--mute);font-size:12px;font-family:var(--font-mono);letter-spacing:0.08em;margin:0 0 12px;">
      sample · ${d.sample} books
    </p>
    <div class="stats-grid">
      <div class="stats-cell">
        <p class="stats-cell__num">${fmtDays(d.median)}</p>
        <p class="stats-cell__lbl">中央値</p>
      </div>
      <div class="stats-cell">
        <p class="stats-cell__num">${fmtDays(d.avg)}</p>
        <p class="stats-cell__lbl">平均</p>
      </div>
      <div class="stats-cell">
        <p class="stats-cell__num">${fmtDays(d.min)}</p>
        <p class="stats-cell__lbl">最短</p>
      </div>
      <div class="stats-cell">
        <p class="stats-cell__num">${fmtDays(d.max)}</p>
        <p class="stats-cell__lbl">最長</p>
      </div>
    </div>
  `;
}
